import * as React from "react";
import { ActionButton, Card } from "@jhool-io/fe-components";
import { cn } from "../../utils/helpers";
import ChevronUpIcon from "../../icons/ChevronUp";
import ChevronDownIcon from "../../icons/ChevronDown";

interface AccordionProps {
    /** Accordion header */
    header: React.ReactNode;
    /** Accordion content */
    content: React.ReactNode;
    /** Optional extra style for content */
    className?: string;
    /** Determines accordion initial state */
    isAccordionMaximised?: boolean;
    /** Optional classname to style card */
    buttonPosition?: "right" | "left";
    cardClass?: string;
    /** Maximize toggle button position */
    /** Accordion variant relative to position of arrow for minimizing and maximizing the accordion */
    variant?: "arrow-fixed" | "arrow-flexed";
    /** Hide accordion arrow */
    hideArrow?: boolean;
}

export default function Accordion({
    header,
    content,
    isAccordionMaximised,
    className,
    cardClass,
    buttonPosition = "right",
    variant = "arrow-fixed",
    hideArrow = false,
}: AccordionProps) {
    const [isMaximised, setIsMaximised] = React.useState<boolean>(
        isAccordionMaximised || false
    );

    const itemsRef = React.useRef<HTMLDivElement>(null);

    const handleToggleMaximizeState = () => {
        setIsMaximised((state) => !state);
    };

    React.useEffect(() => {
        if (itemsRef.current) {
            if (isMaximised) {
                itemsRef.current.style.height = `auto`;
            } else {
                itemsRef.current.style.height = `0`;
            }
        }
    }, [isMaximised]);

    if (variant === "arrow-flexed") {
        return (
            <Card classNames={cn(cardClass)}>
                <div className="flex items-center justify-between gap-x-[--spacing-items] pb-[--spacing-items] [border-bottom:1px_solid_var(--color-stroke)]">
                    {header}
                    {!hideArrow ? (
                        <ActionButton
                            aria-label="nav arrow"
                            size="auto"
                            variant="can-hover"
                            onClick={handleToggleMaximizeState}
                            actionTitle={isMaximised ? "Minimize" : "Maximize"}
                        >
                            {isMaximised ? (
                                <ChevronUpIcon />
                            ) : (
                                <ChevronDownIcon />
                            )}
                        </ActionButton>
                    ) : null}
                </div>
                <div
                    ref={itemsRef}
                    className={cn(
                        "overflow-y-auto transition duration-[0.25s] ease-in-out",
                        className
                    )}
                >
                    {content}
                </div>
            </Card>
        );
    }

    return (
        <Card classNames={cn("relative", cardClass)}>
            <ActionButton
                aria-label="nav arrow"
                variant="can-hover"
                size="auto"
                onClick={handleToggleMaximizeState}
                className={cn("absolute top-0 right-0", {
                    "ml-3 hidden md:absolute md:top-0 md:right-0  md:ml-0":
                        buttonPosition === "left",
                })}
                actionTitle={isMaximised ? "Minimize" : "Maximize"}
            >
                {isMaximised ? (
                    <ChevronUpIcon className="size-4" />
                ) : (
                    <ChevronDownIcon className="size-4" />
                )}
            </ActionButton>
            <div
                className={cn({
                    "block md:flex md:items-center": buttonPosition === "left",
                })}
            >
                {header}
                <ActionButton
                    aria-label="nav arrow"
                    variant="can-hover"
                    size="auto"
                    onClick={handleToggleMaximizeState}
                    className={cn(
                        "absolute top-0 right-0 md:ml-3 max-md:absolute max-md:top-0 max-md:right-0 max-md:ml-0",
                        {
                            hidden: buttonPosition === "right",
                        }
                    )}
                    actionTitle={isMaximised ? "Minimize" : "Maximize"}
                >
                    {isMaximised ? (
                        <ChevronUpIcon className="size-4" />
                    ) : (
                        <ChevronDownIcon className="size-4" />
                    )}
                </ActionButton>
            </div>
            <div
                ref={itemsRef}
                className={cn(
                    "overflow-y-auto transition duration-[0.25s] ease-in-out",
                    className
                )}
            >
                {content}
            </div>
        </Card>
    );
}
